import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { FileSelectorComponent, SelectedFileContent } from '../file-selector/file-selector.component';

export type FileContentTypeCode = 'HTML' | 'JSON' | 'XML' | 'TEXT' | string;

export interface FileContentType {
  id: string;
  description: string;
  extension: string;
  mimeType: string;
  editorLanguage: string;
  allowPreview: boolean;
  allowFormatting: boolean;
  allowEditing: boolean;
}

interface FileContentConfig {
  language: string;
  extensions: string[];
  mimeTypes: string[];
  mimeType: string;
  preview: boolean;
  format: boolean;
  editing: boolean;
}

const CONTENT_CONFIGS: Record<string, FileContentConfig> = {
  HTML: { language: 'html', extensions: ['.html', '.htm'], mimeTypes: ['text/html'], mimeType: 'text/html', preview: true, format: false, editing: true },
  JSON: { language: 'json', extensions: ['.json'], mimeTypes: ['application/json', 'text/json'], mimeType: 'application/json', preview: false, format: true, editing: true },
  XML: { language: 'xml', extensions: ['.xml'], mimeTypes: ['application/xml', 'text/xml'], mimeType: 'application/xml', preview: false, format: true, editing: true },
  TEXT: { language: 'plaintext', extensions: ['.txt'], mimeTypes: ['text/plain'], mimeType: 'text/plain', preview: false, format: false, editing: true },
};

@Component({
  selector: 'idba-file-content-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, MonacoEditorModule, MatButtonModule, MatIconModule, FileSelectorComponent],
  templateUrl: './file-content-editor.component.html',
  styleUrls: ['./file-content-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => FileContentEditorComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => FileContentEditorComponent), multi: true },
  ],
})
export class FileContentEditorComponent implements ControlValueAccessor, Validator {
  private readonly sanitizer = inject(DomSanitizer);
  private typeCode = 'TEXT';
  private dynamicConfig: FileContentConfig | null = null;
  private defaultPreview = false;

  @Input() fileName = '';
  @Input() label = 'Conteúdo do arquivo';
  @Input() showFileName = true;
  @Input() allowUpload = true;
  @Input() allowPreview = true;
  @Input() allowDownload = true;
  @Input() allowClear = true;
  @Input() allowFormat = true;
  @Input()
  set previewByDefault(value: boolean) {
    this.defaultPreview = value;
    if (value) this.previewMode = this.canPreview;
  }

  get previewByDefault(): boolean {
    return this.defaultPreview;
  }
  @Input() previewLabel = 'Pré-visualizar';
  @Input() codeLabel = 'Ver código';
  @Input() maxFileSize?: number;
  @Input() theme = 'vs-dark';

  @Input()
  set contentType(value: FileContentTypeCode | FileContentType | Record<string, unknown> | null | undefined) {
    this.typeCode = this.resolveType(value);
    this.dynamicConfig = this.resolveConfig(value);
    this.previewMode = this.previewByDefault && this.canPreview;
    this.updateEditorOptions();
    this.updatePreview();
    this.onValidatorChange();
  }

  @Output() fileNameChange = new EventEmitter<string>();
  @Output() contentError = new EventEmitter<string>();

  value = '';
  disabled = false;
  previewMode = false;
  fileError = '';
  previewHtml: SafeHtml = this.sanitizer.bypassSecurityTrustHtml('');
  editorOptions: Record<string, unknown> = {};

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  constructor() {
    this.updateEditorOptions();
  }

  get config(): FileContentConfig {
    return this.dynamicConfig ?? CONTENT_CONFIGS[this.typeCode] ?? {
      language: this.typeCode.toLowerCase() || 'plaintext',
      extensions: [],
      mimeTypes: [],
      mimeType: 'text/plain',
      preview: false,
      format: false,
      editing: true,
    };
  }

  get accept(): string {
    return [...this.config.extensions, ...this.config.mimeTypes].join(',');
  }

  get canPreview(): boolean {
    return this.allowPreview && this.config.preview;
  }

  get canFormat(): boolean {
    return this.allowFormat && this.config.format;
  }

  writeValue(value: string | null): void {
    this.value = value ?? '';
    if (this.previewByDefault) this.previewMode = this.canPreview;
    this.updatePreview();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.updateEditorOptions();
  }

  validate(_: AbstractControl): ValidationErrors | null {
    if (!this.value.trim()) return null;
    if (this.typeCode === 'JSON') {
      try { JSON.parse(this.value); } catch { return { invalidJson: true }; }
    }
    if (this.typeCode === 'XML') {
      const document = new DOMParser().parseFromString(this.value, 'application/xml');
      if (document.querySelector('parsererror')) return { invalidXml: true };
    }
    return null;
  }

  markTouched(): void {
    this.onTouched();
  }

  setValue(value: string): void {
    this.value = value ?? '';
    this.fileError = '';
    this.updatePreview();
    this.onChange(this.value);
    this.onTouched();
    this.onValidatorChange();
  }

  receiveFile(selected: SelectedFileContent): void {
    this.fileName = selected.name;
    this.fileNameChange.emit(selected.name);
    this.setValue(selected.content ?? '');
  }

  showFileError(message: string): void {
    this.fileError = message;
    if (message) this.contentError.emit(message);
  }

  formatContent(): void {
    try {
      if (this.typeCode === 'JSON') this.setValue(JSON.stringify(JSON.parse(this.value || '{}'), null, 2));
      else if (this.typeCode === 'XML') this.setValue(this.formatXml(this.value));
    } catch {
      const message = this.typeCode === 'JSON' ? 'O conteúdo JSON é inválido.' : 'O conteúdo XML é inválido.';
      this.showFileError(message);
    }
  }

  toggleMode(): void {
    this.previewMode = !this.previewMode;
    if (this.previewMode) this.updatePreview();
  }

  clear(): void {
    this.fileName = '';
    this.fileNameChange.emit('');
    this.setValue('');
  }

  download(): void {
    if (!this.value) return;
    const extension = this.config.extensions[0] ?? '';
    const currentName = this.fileName.trim() || `conteudo${extension}`;
    const fileName = extension && !this.config.extensions.some((item) => currentName.toLowerCase().endsWith(item))
      ? `${currentName}${extension}`
      : currentName;
    const blob = new Blob([this.value], { type: `${this.config.mimeType};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  }

  private resolveConfig(
    value: FileContentTypeCode | FileContentType | Record<string, unknown> | null | undefined,
  ): FileContentConfig | null {
    if (!value || typeof value === 'string') return null;

    const extension = String(value['extension'] ?? '').trim().replace(/^\./, '').toLowerCase();
    const mimeType = String(value['mimeType'] ?? 'text/plain').trim();
    const language = String(value['editorLanguage'] ?? '').trim();

    if (!extension || !language) return null;

    return {
      language,
      extensions: [`.${extension}`],
      mimeTypes: [mimeType],
      mimeType,
      preview: Boolean(value['allowPreview']),
      format: Boolean(value['allowFormatting']),
      editing: Boolean(value['allowEditing']),
    };
  }

  private resolveType(value: FileContentTypeCode | FileContentType | Record<string, unknown> | null | undefined): string {
    if (typeof value === 'string') return value.trim().toUpperCase() || 'TEXT';
    const raw = value?.['id'] ?? value?.['code'] ?? value?.['description'] ?? value?.['displayWith'];
    return String(raw ?? 'TEXT').trim().toUpperCase();
  }

  private updateEditorOptions(): void {
    this.editorOptions = {
      theme: this.theme,
      language: this.config.language,
      automaticLayout: true,
      minimap: { enabled: false },
      readOnly: this.disabled || !this.config.editing,
      scrollBeyondLastLine: false,
      wordWrap: 'on',
    };
  }

  private updatePreview(): void {
    this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(this.value);
  }

  private formatXml(value: string): string {
    const parsed = new DOMParser().parseFromString(value, 'application/xml');
    if (parsed.querySelector('parsererror')) throw new Error('invalid xml');
    const compact = new XMLSerializer().serializeToString(parsed).replace(/>\s*</g, '><');
    const lines = compact.replace(/(>)(<)(\/?)/g, '$1\n$2$3').split('\n');
    let indent = 0;
    return lines.map((line) => {
      if (/^<\//.test(line)) indent = Math.max(indent - 1, 0);
      const formatted = `${'  '.repeat(indent)}${line}`;
      if (/^<[^!?/][^>]*[^/]?>$/.test(line) && !/<\/[^>]+>$/.test(line)) indent++;
      return formatted;
    }).join('\n');
  }
}
